import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Users, Building2, FileText, CreditCard, AlertTriangle, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "agencies", label: "Agencies" },
  { key: "users", label: "Users" },
  { key: "po", label: "PO Requests" },
  { key: "overage", label: "Overage" },
];

function useAdminData(endpoint) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(endpoint);
      setData(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };
  return { data, error, loading, load };
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="fa-card p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest mb-3">
        <Icon className="w-4 h-4" strokeWidth={1.75} />
        {label}
      </div>
      <div className="font-serif text-3xl font-medium text-[#0B1B3D]">{value ?? "—"}</div>
    </div>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}

function OverviewTab() {
  const { data, error, loading, load } = useAdminData("/admin/stats");
  useEffect(() => { load(); }, []);
  if (loading && !data) return <div className="text-slate-500 text-sm">Loading…</div>;
  if (error) return <div className="text-red-700 bg-red-50 border border-red-200 p-3 text-sm" data-testid="admin-error">{String(error)}</div>;
  const t = data?.totals || {};
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Users" value={t.users} testid="stat-users" />
        <StatCard icon={Building2} label="Agencies" value={t.agencies} testid="stat-agencies" />
        <StatCard icon={FileText} label="Cases" value={t.cases} testid="stat-cases" />
        <StatCard icon={CreditCard} label="Paid transactions" value={t.paid_transactions} testid="stat-paid" />
        <StatCard icon={Users} label="Signups (7d)" value={t.signups_last_7d} testid="stat-signups-7d" />
        <StatCard icon={Building2} label="Trial agencies" value={t.trial_agencies} testid="stat-trial" />
        <StatCard icon={Building2} label="Active agencies" value={t.active_agencies} testid="stat-active" />
        <StatCard icon={FileText} label="Open cases" value={t.open_cases} testid="stat-open-cases" />
      </div>

      <button onClick={load} className="fa-btn-secondary" data-testid="admin-refresh"><RefreshCw className="w-4 h-4" strokeWidth={1.75} /> Refresh</button>
    </div>
  );
}

function AgenciesTab() {
  const { data, error, loading, load } = useAdminData("/admin/agencies");
  useEffect(() => { load(); }, []);
  return (
    <div>
      {error && <div className="text-red-700 bg-red-50 border border-red-200 p-3 text-sm mb-3">{String(error)}</div>}
      {loading && <div className="text-slate-500 text-sm mb-3">Loading…</div>}
      <div className="overflow-x-auto fa-card">
        <table className="w-full text-sm" data-testid="admin-agencies-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="p-3">Agency</th>
              <th className="p-3">Plan</th>
              <th className="p-3">Status</th>
              <th className="p-3">Trial ends</th>
              <th className="p-3">Lead source</th>
              <th className="p-3">Created</th>
            </tr>
          </thead>
          <tbody>
            {(data?.agencies || []).map((a) => (
              <tr key={a.agency_id} className="border-t border-slate-100">
                <td className="p-3">{a.name}</td>
                <td className="p-3 font-mono uppercase text-xs">{a.plan || "—"}</td>
                <td className="p-3">{a.subscription_status || "—"}</td>
                <td className="p-3 font-mono text-xs">{fmt(a.trial_ends_at)}</td>
                <td className="p-3">{a.lead_source || "—"}</td>
                <td className="p-3 font-mono text-xs">{fmt(a.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UsersTab() {
  const { data, error, loading, load } = useAdminData("/admin/users");
  useEffect(() => { load(); }, []);
  return (
    <div>
      {error && <div className="text-red-700 bg-red-50 border border-red-200 p-3 text-sm mb-3">{String(error)}</div>}
      {loading && <div className="text-slate-500 text-sm mb-3">Loading…</div>}
      <div className="overflow-x-auto fa-card">
        <table className="w-full text-sm" data-testid="admin-users-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="p-3">Email</th>
              <th className="p-3">Name</th>
              <th className="p-3">Role</th>
              <th className="p-3">Provider</th>
              <th className="p-3">Agency</th>
              <th className="p-3">Created</th>
            </tr>
          </thead>
          <tbody>
            {(data?.users || []).map((u) => (
              <tr key={u.user_id} className="border-t border-slate-100">
                <td className="p-3 font-mono text-xs">{u.email}</td>
                <td className="p-3">{u.name}</td>
                <td className="p-3">{u.role}</td>
                <td className="p-3">{u.auth_provider || "—"}</td>
                <td className="p-3 font-mono text-xs">{u.agency_id}</td>
                <td className="p-3 font-mono text-xs">{fmt(u.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function POTab() {
  const { data, error, loading, load } = useAdminData("/admin/po-requests");
  useEffect(() => { load(); }, []);
  return (
    <div>
      {error && <div className="text-red-700 bg-red-50 border border-red-200 p-3 text-sm mb-3">{String(error)}</div>}
      {loading && <div className="text-slate-500 text-sm mb-3">Loading…</div>}
      <div className="overflow-x-auto fa-card">
        <table className="w-full text-sm" data-testid="admin-po-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="p-3">PO ID</th>
              <th className="p-3">Agency</th>
              <th className="p-3">Plan</th>
              <th className="p-3">Amount</th>
              <th className="p-3">Contact</th>
              <th className="p-3">Status</th>
              <th className="p-3">Created</th>
            </tr>
          </thead>
          <tbody>
            {(data?.po_requests || []).length === 0 && !loading && (
              <tr><td colSpan={7} className="p-6 text-center text-slate-500 text-sm" data-testid="admin-po-empty">No PO requests yet.</td></tr>
            )}
            {(data?.po_requests || []).map((p) => (
              <tr key={p.po_id} className="border-t border-slate-100">
                <td className="p-3 font-mono text-xs">{p.po_id}</td>
                <td className="p-3">{p.agency_name}</td>
                <td className="p-3 font-mono uppercase text-xs">{p.plan_id}</td>
                <td className="p-3 font-mono">${Number(p.amount || 0).toLocaleString()}</td>
                <td className="p-3">{p.contact_name}<br /><span className="text-xs text-slate-500">{p.contact_email}</span></td>
                <td className="p-3">{p.status}</td>
                <td className="p-3 font-mono text-xs">{fmt(p.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OverageTab() {
  const { data, error, loading, load } = useAdminData("/admin/overage");
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-4">
      {error && <div className="text-red-700 bg-red-50 border border-red-200 p-3 text-sm">{String(error)}</div>}
      {loading && <div className="text-slate-500 text-sm">Loading…</div>}
      {data && (
        <div className="fa-card p-5">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest mb-3">
            <AlertTriangle className="w-4 h-4" strokeWidth={1.75} />
            Overage summary (invoice manually)
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><div className="text-xs text-slate-500">Total agencies</div><div className="font-serif text-2xl">{data.count}</div></div>
            <div><div className="text-xs text-slate-500">Over included</div><div className="font-serif text-2xl" data-testid="overage-agencies-over">{data.agencies_over_included}</div></div>
            <div><div className="text-xs text-slate-500">Cases included / yr</div><div className="font-serif text-2xl">{data.included_per_year}</div></div>
            <div><div className="text-xs text-slate-500">Total overage due</div><div className="font-serif text-2xl" data-testid="overage-total">${Number(data.total_overage_amount_due).toLocaleString()}</div></div>
          </div>
        </div>
      )}
      <div className="overflow-x-auto fa-card">
        <table className="w-full text-sm" data-testid="admin-overage-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="p-3">Agency</th>
              <th className="p-3">Plan</th>
              <th className="p-3">Cycle</th>
              <th className="p-3 text-right">Used</th>
              <th className="p-3 text-right">Included</th>
              <th className="p-3 text-right">Overage</th>
              <th className="p-3 text-right">Amount due</th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).length === 0 && !loading && (
              <tr><td colSpan={7} className="p-6 text-center text-slate-500 text-sm">No agencies yet.</td></tr>
            )}
            {(data?.rows || []).map((r) => (
              <tr key={r.agency_id} className={`border-t border-slate-100 ${r.overage_cases > 0 ? "bg-amber-50/40" : ""}`}>
                <td className="p-3">{r.name}</td>
                <td className="p-3 font-mono uppercase text-xs">{r.plan || "—"}</td>
                <td className="p-3 font-mono text-xs">{fmt(r.cycle_start)} → {fmt(r.cycle_end)}</td>
                <td className="p-3 text-right font-mono">{r.cases_used}</td>
                <td className="p-3 text-right font-mono">{r.cases_included}</td>
                <td className="p-3 text-right font-mono">{r.overage_cases > 0 ? <span className="text-amber-800 font-medium">{r.overage_cases}</span> : "—"}</td>
                <td className="p-3 text-right font-mono">{r.overage_amount_due > 0 ? `$${Number(r.overage_amount_due).toLocaleString()}` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  return (
    <AppLayout>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8" data-testid="admin-dashboard">
        <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest mb-2">
          <ShieldCheck className="w-4 h-4" strokeWidth={1.75} />
          Admin
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight font-medium mb-6">Platform administration</h1>

        <div className="border-b border-slate-200 mb-6 flex flex-wrap gap-1" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab === t.key ? "border-[#0B1B3D] text-[#0B1B3D] font-medium" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && <OverviewTab />}
        {tab === "agencies" && <AgenciesTab />}
        {tab === "users" && <UsersTab />}
        {tab === "po" && <POTab />}
        {tab === "overage" && <OverageTab />}

        <div className="mt-10 text-xs text-slate-500">
          Access is restricted to emails in the <code>ADMIN_EMAILS</code> environment variable. <Link to="/dashboard" className="underline">← Back to dashboard</Link>
        </div>
      </div>
    </AppLayout>
  );
}
